import { createContext, useContext, useState, useRef, useEffect, ReactNode, useCallback } from "react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface ATCFeed {
  stream_url: string;
  airport_code: string;
  airport_name: string;
  feed_type?: string;
}

interface AudioContextType {
  // Playback state
  isPlaying: boolean;
  
  // Volume controls
  musicVolume: number;
  soundVolume: number;
  
  // ATC stream
  atcStreamUrl: string | null;
  isAtcActive: boolean;
  isAtcLoading: boolean;
  currentAirportCode: string | null;
  
  // Controls
  playPause: () => void;
  setMusicVolume: (volume: number) => void;
  setSoundVolume: (volume: number) => void;
  setAtcStream: (url: string | null, airportCode?: string) => Promise<void>;
  toggleAtc: () => void;
}

const AudioContext = createContext<AudioContextType | undefined>(undefined);

export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error("useAudio must be used within AudioProvider");
  }
  return context;
};

interface AudioProviderProps {
  children: ReactNode;
}

export const AudioProvider = ({ children }: AudioProviderProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [musicVolume, setMusicVolumeState] = useState(70);
  const [soundVolume, setSoundVolumeState] = useState(50);
  const [atcStreamUrl, setAtcStreamUrl] = useState<string | null>(null);
  const [isAtcActive, setIsAtcActive] = useState(false);
  const [isAtcLoading, setIsAtcLoading] = useState(false);
  const [currentAirportCode, setCurrentAirportCode] = useState<string | null>(null);
  const [hasAutoConnected, setHasAutoConnected] = useState(false);
  
  const atcAudioRef = useRef<HTMLAudioElement | null>(null);
  const isPlayingRef = useRef(false);
  const isAtcActiveRef = useRef(false);
  const currentAirportCodeRef = useRef<string | null>(null);
  const failedUrlsRef = useRef<Set<string>>(new Set());
  const isConnectingRef = useRef(false);
  
  // Keep refs in sync
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);
  
  useEffect(() => {
    isAtcActiveRef.current = isAtcActive;
  }, [isAtcActive]);
  
  useEffect(() => {
    currentAirportCodeRef.current = currentAirportCode;
  }, [currentAirportCode]);

  // Find and connect to a working ATC feed
  const findWorkingAtcFeed = useCallback(async (excludeAirport?: string): Promise<boolean> => {
    if (isConnectingRef.current) {
      console.log('Already connecting to ATC, skipping...');
      return false;
    }
    
    isConnectingRef.current = true;
    setIsAtcLoading(true);
    
    try {
      console.log('Finding working ATC feed...', excludeAirport ? `excluding ${excludeAirport}` : '');
      
      // Prioritize tower feeds as they tend to have more activity
      let query = supabase
        .from('atc_feeds')
        .select('stream_url, airport_code, airport_name, feed_type')
        .eq('is_active', true)
        .order('feed_type', { ascending: true }) // tower comes before approach alphabetically
        .limit(40);
      
      if (excludeAirport) {
        query = query.neq('airport_code', excludeAirport);
      }
      
      const { data: feeds } = await query;
      
      if (!feeds || feeds.length === 0) {
        console.error('No ATC feeds available');
        setIsAtcLoading(false);
        isConnectingRef.current = false;
        return false;
      }
      
      // Filter out already failed URLs
      const availableFeeds = feeds.filter(f => !failedUrlsRef.current.has(f.stream_url));
      
      if (availableFeeds.length === 0) {
        console.log('All feeds have failed, clearing failed list and retrying...');
        failedUrlsRef.current.clear();
        const randomFeed = feeds[Math.floor(Math.random() * feeds.length)];
        return await tryConnectToFeed(randomFeed.stream_url, randomFeed.airport_code, randomFeed.airport_name);
      }
      
      // Prioritize tower feeds (more likely to have active chatter)
      const towerFeeds = availableFeeds.filter(f => f.feed_type === 'tower');
      const otherFeeds = availableFeeds.filter(f => f.feed_type !== 'tower');
      
      // Try tower feeds first, then other feeds
      const orderedFeeds = [...towerFeeds, ...otherFeeds];
      
      // Shuffle within each priority group for variety
      const shuffledTower = towerFeeds.sort(() => Math.random() - 0.5);
      const shuffledOther = otherFeeds.sort(() => Math.random() - 0.5);
      const shuffled = [...shuffledTower, ...shuffledOther];
      
      for (const feed of shuffled.slice(0, 8)) {
        console.log(`Trying ${feed.airport_code} (${feed.airport_name})...`);
        const success = await tryConnectToFeed(feed.stream_url, feed.airport_code, feed.airport_name);
        if (success) {
          return true;
        }
        failedUrlsRef.current.add(feed.stream_url);
      }
      
      console.error('Could not connect to any ATC feed');
      setIsAtcLoading(false);
      isConnectingRef.current = false;
      return false;
    } catch (error) {
      console.error('Error finding ATC feed:', error);
      setIsAtcLoading(false);
      isConnectingRef.current = false;
      return false;
    }
  }, []);

  // Try to connect to a specific feed with timeout
  const tryConnectToFeed = useCallback(async (url: string, airportCode: string, airportName: string): Promise<boolean> => {
    return new Promise((resolve) => {
      // Clean up existing audio
      if (atcAudioRef.current) {
        atcAudioRef.current.pause();
        atcAudioRef.current.src = '';
        atcAudioRef.current = null;
      }
      
       // Try direct playback first (bypasses proxy rate limits)
       // Fall back to proxy if CORS blocks direct access
       const tryDirectFirst = url.includes('liveatc.net');
       const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
       const proxiedUrl = `${supabaseUrl}/functions/v1/atc-proxy?url=${encodeURIComponent(url)}`;
      
       const attemptConnection = (useProxy: boolean): Promise<boolean> => {
         return new Promise((resolveAttempt) => {
           const targetUrl = useProxy ? proxiedUrl : url;
           console.log(`Trying ${airportCode} ${useProxy ? 'via proxy' : 'direct'}...`);
           
           const audio = new Audio(targetUrl);
      audio.preload = "auto";
      const calculatedVolume = soundVolume / 100;
      if (isFinite(calculatedVolume)) {
        audio.volume = calculatedVolume;
      }
      
      let resolved = false;
      const timeout = setTimeout(() => {
        if (!resolved) {
          resolved = true;
             console.log(`Timeout connecting to ${airportCode} ${useProxy ? 'via proxy' : 'direct'}`);
          audio.pause();
          audio.src = '';
             resolveAttempt(false);
        }
      }, 8000);
      
      const cleanup = () => {
        clearTimeout(timeout);
      };
      
      audio.addEventListener('canplay', () => {
        if (!resolved) {
          resolved = true;
          cleanup();
             console.log(`Successfully connected to ${airportCode} ${useProxy ? 'via proxy' : 'direct'}`);
          
          atcAudioRef.current = audio;
          setAtcStreamUrl(url);
          setCurrentAirportCode(airportCode);
          setIsAtcLoading(false);
          isConnectingRef.current = false;
          
          // Set up ongoing error handling for this connection
          setupStreamErrorHandling(audio, airportCode);
          
          toast({
            title: `Connected to ${airportCode}`,
            description: airportName,
          });
          
             resolveAttempt(true);
        }
      });
      
      audio.addEventListener('error', () => {
        if (!resolved) {
          resolved = true;
          cleanup();
             console.log(`Failed to connect to ${airportCode} ${useProxy ? 'via proxy' : 'direct'}`);
          audio.src = '';
             resolveAttempt(false);
        }
      });
      
      audio.load();
         });
       };
       
       // Try direct first if it's a LiveATC stream, then fall back to proxy
       const tryConnection = async () => {
         if (tryDirectFirst) {
           const directSuccess = await attemptConnection(false);
           if (directSuccess) {
             resolve(true);
             return;
           }
           // Fall back to proxy
           const proxySuccess = await attemptConnection(true);
           resolve(proxySuccess);
         } else {
           // Non-LiveATC URL, use proxy directly
           const proxySuccess = await attemptConnection(true);
           resolve(proxySuccess);
         }
       };
       
       tryConnection();
    });
  }, [soundVolume]);

  // Set up error handling for an active stream
  const setupStreamErrorHandling = useCallback((audio: HTMLAudioElement, airportCode: string) => {
    let reconnectAttempts = 0;
    const maxReconnectAttempts = 2;
    
    const handleStreamFailure = async () => {
      console.log(`Stream failure for ${airportCode}, finding new feed...`);
      failedUrlsRef.current.add(audio.src);
      
      if (isPlayingRef.current) {
        const success = await findWorkingAtcFeed(airportCode);
        if (success && atcAudioRef.current) {
          setIsAtcActive(true);
          atcAudioRef.current.play().catch(console.error);
        }
      }
    };
    
    audio.addEventListener('error', () => {
      console.error(`ATC stream error for ${airportCode}`);
      handleStreamFailure();
    });
    
    audio.addEventListener('stalled', () => {
      console.warn(`ATC stream stalled for ${airportCode}`);
      reconnectAttempts++;
      if (reconnectAttempts >= maxReconnectAttempts) {
        handleStreamFailure();
      }
    });
    
    audio.addEventListener('ended', () => {
      console.log(`ATC stream ended for ${airportCode}`);
      handleStreamFailure();
    });
    
    audio.addEventListener('playing', () => {
      reconnectAttempts = 0;
      setIsAtcActive(true);
      setIsAtcLoading(false);
    });
    
    audio.addEventListener('waiting', () => {
      setIsAtcLoading(true);
    });
    
    audio.addEventListener('canplay', () => {
      setIsAtcLoading(false);
    });
  }, [findWorkingAtcFeed]);

  // Auto-connect to ATC on page load
  useEffect(() => {
    if (hasAutoConnected) return;
    
    const autoConnectATC = async () => {
      console.log('Auto-connecting to ATC on page load...');
      setHasAutoConnected(true);
      await findWorkingAtcFeed();
    };
    
    const timer = setTimeout(autoConnectATC, 1000);
    return () => clearTimeout(timer);
  }, [hasAutoConnected, findWorkingAtcFeed]);

  // Handle visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && isPlayingRef.current && atcAudioRef.current) {
        if (isAtcActiveRef.current && atcAudioRef.current.paused) {
          atcAudioRef.current.play().catch(console.error);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Update ATC volume
  useEffect(() => {
    if (atcAudioRef.current) {
      const calculatedVolume = soundVolume / 100;
      if (isFinite(calculatedVolume)) {
        atcAudioRef.current.volume = calculatedVolume;
      }
    }
  }, [soundVolume]);

  const playPause = useCallback(async () => {
    console.log('PlayPause clicked, current state:', isPlaying);
    
    if (isPlaying) {
      atcAudioRef.current?.pause();
      setIsPlaying(false);
      setIsAtcActive(false);
    } else {
      setIsPlaying(true);
      
      // If no ATC connected or not ready, find one
      if (!atcAudioRef.current || !atcStreamUrl) {
        console.log('No ATC connected, finding working feed...');
        const success = await findWorkingAtcFeed();
        if (success && atcAudioRef.current) {
          setIsAtcActive(true);
          atcAudioRef.current.play().catch(async (e) => {
            console.error('Error playing ATC after connect:', e);
            // Try another feed
            const retrySuccess = await findWorkingAtcFeed(currentAirportCodeRef.current || undefined);
            if (retrySuccess && atcAudioRef.current) {
              atcAudioRef.current.play().catch(console.error);
            }
          });
        }
      } else {
        // Try to play existing connection
        setIsAtcActive(true);
        try {
          await atcAudioRef.current.play();
        } catch (e) {
          console.error('Error playing existing ATC:', e);
          // Current stream failed, find a new one
          const success = await findWorkingAtcFeed(currentAirportCodeRef.current || undefined);
          if (success && atcAudioRef.current) {
            setIsAtcActive(true);
            atcAudioRef.current.play().catch(console.error);
          }
        }
      }
    }
  }, [isPlaying, atcStreamUrl, findWorkingAtcFeed]);

  const setAtcStream = useCallback(async (url: string | null, airportCode?: string) => {
    console.log('Setting ATC stream:', url, 'Airport:', airportCode);
    
    if (!url) {
      if (atcAudioRef.current) {
        atcAudioRef.current.pause();
        atcAudioRef.current = null;
      }
      setAtcStreamUrl(null);
      setIsAtcActive(false);
      setCurrentAirportCode(null);
      return;
    }
    
    if (airportCode) {
      const success = await tryConnectToFeed(url, airportCode, airportCode);
      if (!success) {
        // User explicitly selected this feed - don't auto-failover, show error
        toast({
          title: `Could not connect to ${airportCode}`,
          description: "This stream may be temporarily unavailable. Try another airport.",
          variant: "destructive",
        });
        setIsAtcLoading(false);
      }
    }
  }, [tryConnectToFeed]);

  const toggleAtc = useCallback(async () => {
    console.log('Toggle ATC, current state:', isAtcActive);
    
    if (isAtcActive) {
      atcAudioRef.current?.pause();
      setIsAtcActive(false);
    } else {
      if (!atcAudioRef.current) {
        const success = await findWorkingAtcFeed();
        if (success && atcAudioRef.current) {
          if (!isPlaying) setIsPlaying(true);
          setIsAtcActive(true);
          atcAudioRef.current.play().catch(console.error);
        }
      } else {
        if (!isPlaying) setIsPlaying(true);
        setIsAtcActive(true);
        try {
          await atcAudioRef.current.play();
        } catch (e) {
          console.error('Error playing ATC:', e);
          const success = await findWorkingAtcFeed(currentAirportCodeRef.current || undefined);
          if (success && atcAudioRef.current) {
            atcAudioRef.current.play().catch(console.error);
          }
        }
      }
    }
  }, [isAtcActive, isPlaying, findWorkingAtcFeed]);

  const value: AudioContextType = {
    isPlaying,
    musicVolume,
    soundVolume,
    atcStreamUrl,
    isAtcActive,
    isAtcLoading,
    currentAirportCode,
    playPause,
    setMusicVolume: setMusicVolumeState,
    setSoundVolume: setSoundVolumeState,
    setAtcStream,
    toggleAtc,
  };

  return <AudioContext.Provider value={value}>{children}</AudioContext.Provider>;
};
